import { CircularProgress } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Checkbox from "@saleor/components/Checkbox";
import FormSpacer from "@saleor/components/FormSpacer";
import Hr from "@saleor/components/Hr";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import { SearchMalls_search_edges_node } from "@saleor/searches/types/SearchMalls";
import useScrollableDialogStyle from "@saleor/styles/useScrollableDialogStyle";
import { FetchMoreProps } from "@saleor/types";
import React, { useEffect } from "react";
import InfiniteScroll from "react-infinite-scroller";
import { FormattedMessage, useIntl } from "react-intl";

import { StaffDetailsFormData } from "../StaffCreatePage";

export interface StaffAssignMallDialog extends FetchMoreProps {
  malls: SearchMalls_search_edges_node[];
  fetchMoreMalls?: FetchMoreProps;
  field: string;
  initial: any;
  open: boolean;
  onFetch: (value: string) => void;
  onClose: () => void;
  onConfirm: (data: SearchMalls_search_edges_node[]) => void;
  onSubmit?: () => void;
  formData: StaffDetailsFormData;
  appliedToAllMalls: boolean;
}

const useStyles = makeStyles(
  theme => ({
    checkboxCell: {
      paddingLeft: 0
    },
    container: {
      maxHeight: 500
    },
    heading: {
      marginBottom: theme.spacing(2),
      marginTop: theme.spacing(2)
    },
    wideCell: {
      width: "100%"
    }
  }),
  { name: "StaffAssignMallDialog" }
);

const StaffAssignMallDialog: React.FC<StaffAssignMallDialog> = props => {
  const {
    onClose,
    malls,
    open,
    initial,
    onFetchMore,
    onConfirm,
    hasMore,
    appliedToAllMalls
  } = props;
  const classes = useStyles(props);
  const intl = useIntl();
  const scrollableDialogClasses = useScrollableDialogStyle({});
  const [selectedMalls, setSelectedMalls] = React.useState(
    initial?.map(mall => ({ id: mall?.id, name: mall?.name }))
  );
  const [selectAllChecked, setSelectAllChecked] = React.useState(
    appliedToAllMalls
  );
  const [query, setQuery] = React.useState("");
  const [uniqueMalls, setUniqueMalls] = React.useState([]);

  const toggleMallSelection = (id, name) => {
    setSelectedMalls(prevSelected => {
      const index = prevSelected.findIndex(mall => mall.id === id);
      let newSelected;
      if (index === -1) {
        newSelected = [...prevSelected, { id, name }];
      } else {
        newSelected = prevSelected.filter(mall => mall.id !== id);
      }
      return newSelected;
    });
  };

  const handleSelectAll = selectAll => {
    setSelectAllChecked(selectAll);
    if (selectAll) {
      setSelectedMalls(
        uniqueMalls.map(mall => ({ id: mall.id, name: mall.name }))
      );
    } else {
      setSelectedMalls([]);
    }
  };

  const handleSubmit = () => {
    const mallDataToPass = selectAllChecked ? [] : selectedMalls;
    onConfirm(mallDataToPass);
    handleClose();
    if (props.onSubmit) {
      props.onSubmit();
    }
  };

  const handleClose = () => {
    setQuery("");
    onClose();
  };

  useEffect(() => {
    const newUniqueMalls = malls.map(mall => ({
      ...mall,
      isChecked: selectedMalls.some(selected => selected.id === mall.id)
    }));
    setUniqueMalls(newUniqueMalls);
  }, [malls, selectedMalls]);

  useEffect(() => {
    setSelectedMalls(
      appliedToAllMalls
        ? malls.map(mall => ({ id: mall?.id, name: mall?.name }))
        : initial.map(mall => ({ id: mall?.id, name: mall?.name }))
    );
  }, [initial, appliedToAllMalls, malls]);

  useEffect(() => {
    setSelectAllChecked(
      selectedMalls.length === uniqueMalls.length && uniqueMalls.length > 0
    );
  }, [selectedMalls, uniqueMalls]);

  return (
    <Dialog onClose={handleClose} open={open} fullWidth maxWidth="sm">
      <DialogTitle>
        <FormattedMessage
          defaultMessage="Assign Malls"
          description="dialog header"
        />
      </DialogTitle>
      <DialogContent>
        <Typography>
          <FormattedMessage defaultMessage="Choose malls, you want users to be limited to, from the list below" />
        </Typography>
        <FormSpacer />
        <TextField
          id="filterMallsTxt"
          value={query}
          onChange={e => setQuery(e.target.value)}
          label={intl.formatMessage({
            defaultMessage: "Filter malls",
            description: "search box label"
          })}
          placeholder={intl.formatMessage({
            defaultMessage: "Search by mall name",
            description: "search box placeholder"
          })}
          fullWidth
        />
      </DialogContent>
      <Hr />
      <DialogContent className={classes.container}>
        <Typography className={classes.heading} variant="subtitle1">
          <FormattedMessage
            defaultMessage="Malls A to Z"
            description="mall selection"
          />
        </Typography>
        <InfiniteScroll
          pageStart={0}
          loadMore={onFetchMore}
          hasMore={hasMore}
          useWindow={false}
          loader={
            <div
              className={scrollableDialogClasses.loadMoreLoaderContainer}
              key={0}
            >
              <CircularProgress size={16} />
            </div>
          }
          threshold={10}
        >
          <ResponsiveTable>
            <TableBody>
              {!query && (
                <TableRow key="all">
                  <TableCell className={classes.wideCell}>Select All</TableCell>
                  <TableCell
                    padding="checkbox"
                    className={classes.checkboxCell}
                  >
                    <Checkbox
                      checked={selectAllChecked}
                      onChange={e => handleSelectAll(e.target.checked)}
                    />
                  </TableCell>
                </TableRow>
              )}
              {uniqueMalls
                .filter(mall =>
                  mall.name.toLowerCase().includes(query.toLowerCase())
                )
                .map((mall, index) => (
                  <TableRow key={mall?.id} id={`mallRow${index}`}>
                    <TableCell className={classes.wideCell}>
                      {mall?.name}
                    </TableCell>
                    <TableCell
                      padding="checkbox"
                      className={classes.checkboxCell}
                    >
                      <Checkbox
                        checked={selectedMalls.some(
                          selected => selected.id === mall.id
                        )}
                        onChange={() => toggleMallSelection(mall.id, mall.name)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </ResponsiveTable>
        </InfiniteScroll>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSubmit} color="primary">
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StaffAssignMallDialog;
